// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  PolicyListDTO,
  PolicyDetailDTO,
  PolicySignatoryDTO,
  UpdatePolicySignatoryRequest,
  PolicyListFilters,
  Page,
} from '../types/policyList';

export const policyAggregateApi = {
  /**
   * Get paginated policy list for advisor with filters
   * When filters.all is true, fetches all policies across the brokerage (admin view)
   */
  getPolicies: async (filters: PolicyListFilters = {}): Promise<Page<PolicyListDTO>> => {
    const params: Record<string, string | number | boolean | undefined> = {
      page: filters.page ?? 0,
      size: filters.size ?? 20,
    };

    if (filters.searchQuery) params.searchQuery = filters.searchQuery;
    if (filters.category) params.category = filters.category;
    if (filters.providerId) params.providerId = filters.providerId;
    if (filters.policyTypeId) params.policyTypeId = filters.policyTypeId;
    if (filters.profileId) params.profileId = filters.profileId;
    if (filters.status) params.status = filters.status;
    if (filters.ownershipType) params.ownershipType = filters.ownershipType;
    if (filters.sortBy) params.sort = `${filters.sortBy},${filters.sortDirection || 'asc'}`;
    // Admin view parameters
    if (filters.all) params.all = true;
    if (filters.advisorId) params.advisorId = filters.advisorId;

    const response = await api.get<Page<PolicyListDTO>>('/policies', {
      params: {
        ...params,
        // NOTE: PolicyAggregateController does not yet bind `tagIds` — see
        // the type-level comment on PolicyListFilters.tagIds. Sent anyway
        // so the client filter UI is forward-compatible with zero UI churn
        // once the backend gains support.
        tagIds: filters.tagIds?.length ? filters.tagIds : undefined,
      },
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  /**
   * Get detailed policy information with signatories
   */
  getPolicyDetail: async (policyId: string): Promise<PolicyDetailDTO> => {
    const response = await api.get<PolicyDetailDTO>(`/policies/${policyId}`);
    return response.data;
  },

  /**
   * Update a policy signatory's relationship type or other attributes
   */
  updateSignatory: async (
    policyId: string,
    signatoryId: string,
    data: UpdatePolicySignatoryRequest
  ): Promise<PolicySignatoryDTO> => {
    const response = await api.put<PolicySignatoryDTO>(
      `/policies/${policyId}/signatories/${signatoryId}`,
      data
    );
    return response.data;
  },

  /**
   * Remove a signatory from a policy
   */
  removeSignatory: async (policyId: string, signatoryId: string): Promise<void> => {
    await api.delete(`/policies/${policyId}/signatories/${signatoryId}`);
  },
};
